/**
 * HTX (Huobi) REST API Functions
 * @see https://huobiapi.github.io/docs/spot/v1/en/
 */

import type {
  HtxDepthResponse,
  HtxErrorResponse,
  HtxKlinesResponse,
  HtxSymbolInfo,
  HtxTickerResponse,
  HtxTradeRestResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://api.huobi.pro';

export const htxRestEndpoints = {
  symbols: `${BASE_URL}/v1/common/symbols`,
  klines: `${BASE_URL}/market/history/kline`,
  trades: `${BASE_URL}/market/history/trade`,
  ticker: `${BASE_URL}/market/detail/merged`,
  depth: `${BASE_URL}/market/depth`,
} as const;

// Helper functions
export function formatHtxSymbol(base: string, quote: string): string {
  return `${base}${quote}`.toLowerCase();
}

export function parseHtxSymbol(
  symbol: string,
  knownQuotes: string[] = ['USDT', 'USDC', 'BTC', 'ETH', 'HUSD', 'HT', 'TRX'],
): { base: string; quote: string } | null {
  const upperSymbol = symbol.toUpperCase();
  const sortedQuotes = [...knownQuotes].sort((a, b) => b.length - a.length);

  for (const quote of sortedQuotes) {
    if (upperSymbol.endsWith(quote)) {
      const base = upperSymbol.slice(0, -quote.length);
      if (base.length > 0) {
        return { base, quote };
      }
    }
  }
  return null;
}

export function htxTimeToMs(seconds: number): number {
  return seconds * 1000;
}

export function msToHtxTime(ms: number): number {
  return Math.floor(ms / 1000);
}

export function precisionToTickSize(precision: number): string {
  return Math.pow(10, -precision).toFixed(precision);
}

export function formatHtxPrice(price: number, precision: number): string {
  return price.toFixed(precision);
}

export function normalizeHtxKlinesOrder<T extends { id: number }>(klines: T[]): T[] {
  if (klines.length < 2) return klines;
  const firstTime = klines[0].id;
  const lastTime = klines[klines.length - 1].id;
  return firstTime > lastTime ? [...klines].reverse() : klines;
}

export function validateHtxResponse<T extends { status: string }>(
  response: T,
  operation: string,
): void {
  if (response.status !== 'ok') {
    const error = response as unknown as HtxErrorResponse;
    const errMsg = error['err-msg'];
    const errCode = error['err-code'];
    const baseMessage = errMsg ? errMsg : `status=${response.status}`;
    throw new Error(
      `HTX ${operation} failed: ${baseMessage}` + (errCode ? `, code=${errCode}` : ''),
    );
  }
}

export interface HtxSymbolsResponse {
  status: 'ok' | 'error';
  data: HtxSymbolInfo[];
  'err-code'?: string;
  'err-msg'?: string;
}

export const getHtxSymbols = async (options?: RestRequestOptions): Promise<HtxSymbolsResponse> => {
  const response = await get<HtxSymbolsResponse>({
    url: htxRestEndpoints.symbols,
    options,
  });
  validateHtxResponse(response.data, 'getHtxSymbols');
  return response.data;
};

export interface HtxKlinesRequest {
  symbol: string;
  period: string;
  size?: number;
  from?: number;
  to?: number;
}

export const getHtxKlines = async (
  params: HtxKlinesRequest,
  options?: RestRequestOptions,
): Promise<HtxKlinesResponse> => {
  const queryParams: Record<string, string | number> = {
    symbol: params.symbol,
    period: params.period,
  };

  if (params.size !== undefined) {
    queryParams.size = params.size;
  } else if (params.from !== undefined || params.to !== undefined) {
    if (params.from !== undefined) queryParams.from = params.from;
    if (params.to !== undefined) queryParams.to = params.to;
  }

  const response = await get<HtxKlinesResponse>({
    url: htxRestEndpoints.klines,
    params: queryParams,
    options,
  });
  validateHtxResponse(response.data, 'getHtxKlines');
  return response.data;
};

export interface HtxTickerRequest {
  symbol: string;
}

export const getHtxTicker = async (
  params: HtxTickerRequest,
  options?: RestRequestOptions,
): Promise<HtxTickerResponse> => {
  const response = await get<HtxTickerResponse>({
    url: htxRestEndpoints.ticker,
    params: { symbol: params.symbol },
    options,
  });
  validateHtxResponse(response.data, 'getHtxTicker');
  return response.data;
};

export interface HtxTradesRequest {
  symbol: string;
  size?: number;
}

export const getHtxTrades = async (
  params: HtxTradesRequest,
  options?: RestRequestOptions,
): Promise<HtxTradeRestResponse> => {
  const response = await get<HtxTradeRestResponse>({
    url: htxRestEndpoints.trades,
    params: { symbol: params.symbol, size: params.size ?? 1 },
    options,
  });
  validateHtxResponse(response.data, 'getHtxTrades');
  return response.data;
};

export interface HtxDepthRequest {
  symbol: string;
  type?: string;
  depth?: number;
}

export const getHtxDepth = async (
  params: HtxDepthRequest,
  options?: RestRequestOptions,
): Promise<HtxDepthResponse> => {
  const response = await get<HtxDepthResponse>({
    url: htxRestEndpoints.depth,
    params: { symbol: params.symbol, type: params.type ?? 'step0', depth: params.depth ?? 20 },
    options,
  });
  validateHtxResponse(response.data, 'getHtxDepth');
  return response.data;
};
